import type { AoMeta } from './types';

/**
 * The terrain grid for one area of operations (AO). Cells are square; row 0 is the south edge, column 0 the west edge.
 * Positions elsewhere in the sim are integer centimeters from the south-west corner.
 */
export class Grid {
  readonly w: number;
  readonly h: number;
  readonly cellM: number;
  readonly cellCm: number;

  constructor(
    readonly meta: AoMeta,
    readonly cost: Uint8Array,
    readonly cover: Uint8Array,
    readonly elev: Int16Array,
    readonly roads: Uint8Array,
  ) {
    this.w = meta.w; this.h = meta.h; this.cellM = meta.cellM; this.cellCm = meta.cellM * 100;
    const n = this.w * this.h;
    if (cost.length !== n || cover.length !== n || elev.length !== n || roads.length !== n) {
      throw new Error(`Grid buffers do not match ${this.w}x${this.h}`);
    }
  }

  static fromBuffers(meta: AoMeta, b: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer }): Grid {
    return new Grid(meta, new Uint8Array(b.cost), new Uint8Array(b.cover), new Int16Array(b.elev), new Uint8Array(b.roads));
  }

  /** A synthetic grid for tests. Default: flat open ground (cost 4, cover 40, elev 0). */
  static synthetic(
    w: number, h: number, cellM: number,
    fill: (cx: number, cy: number) => { cost: number; cover: number; elev: number; road: number } = () => ({ cost: 4, cover: 40, elev: 0, road: 0 }),
  ): Grid {
    const n = w * h;
    const cost = new Uint8Array(n), cover = new Uint8Array(n), elev = new Int16Array(n), roads = new Uint8Array(n);
    for (let cy = 0; cy < h; cy++) for (let cx = 0; cx < w; cx++) {
      const v = fill(cx, cy); const i = cy * w + cx;
      cost[i] = v.cost; cover[i] = v.cover; elev[i] = v.elev; roads[i] = v.road;
    }
    const meta: AoMeta = {
      name: 'synthetic', title: 'Synthetic', lat: 0, lon: 0, sizeM: w * cellM, cellM, w, h,
      originLat: 0, originLon: 0, mPerDegLat: 111320, mPerDegLon: 111320,
    };
    return new Grid(meta, cost, cover, elev, roads);
  }

  index(cx: number, cy: number): number { return cy * this.w + cx; }
  cx(i: number): number { return i % this.w; }
  cy(i: number): number { return (i / this.w) | 0; }
  inBounds(cx: number, cy: number): boolean { return cx >= 0 && cy >= 0 && cx < this.w && cy < this.h; }

  cellOfCm(x: number, y: number): number {
    let cx = Math.floor(x / this.cellCm), cy = Math.floor(y / this.cellCm);
    if (cx < 0) cx = 0; else if (cx >= this.w) cx = this.w - 1;
    if (cy < 0) cy = 0; else if (cy >= this.h) cy = this.h - 1;
    return this.index(cx, cy);
  }

  centerCm(i: number): { x: number; y: number } {
    return { x: this.cx(i) * this.cellCm + (this.cellCm >> 1), y: this.cy(i) * this.cellCm + (this.cellCm >> 1) };
  }

  passable(i: number): boolean { return this.cost[i] > 0; }
  costAt(i: number): number { return this.cost[i]; }
  coverAt(i: number): number { return this.cover[i]; }
  elevAt(i: number): number { return this.elev[i]; }
  roadAt(i: number): number { return this.roads[i]; }

  /** Elevation in meters at a centimeter position (nearest cell). */
  elevAtCm(x: number, y: number): number { return this.elev[this.cellOfCm(x, y)]; }

  /** Integer Euclidean distance in centimeters. */
  distCm(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = a.x - b.x, dy = a.y - b.y;
    return Math.round(Math.sqrt(dx * dx + dy * dy));
  }

  /**
   * Line of sight between two positions with observer/target heights (meters) above their terrain.
   * Walks the cells along the segment; blocked if the terrain at any intermediate cell rises above the sight line.
   */
  hasLos(fromCm: { x: number; y: number }, hFrom: number, toCm: { x: number; y: number }, hTo: number): boolean {
    const c0 = this.cellOfCm(fromCm.x, fromCm.y), c1 = this.cellOfCm(toCm.x, toCm.y);
    if (c0 === c1) return true;
    let x0 = this.cx(c0), y0 = this.cy(c0);
    const x1 = this.cx(c1), y1 = this.cy(c1);
    const z0 = this.elev[c0] + hFrom, z1 = this.elev[c1] + hTo;
    const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
    const steps = Math.max(dx, dy);
    let err = dx - dy, step = 0;
    while (true) {
      if (x0 === x1 && y0 === y1) return true;
      const e2 = err * 2;
      if (e2 > -dy) { err -= dy; x0 += sx; }
      if (e2 < dx) { err += dx; y0 += sy; }
      step++;
      if (x0 === x1 && y0 === y1) return true;
      const f = step / steps;
      const lineZ = z0 + (z1 - z0) * f;
      if (this.elev[this.index(x0, y0)] > lineZ) return false;
    }
  }
}
